import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useGoogleMaps } from '../../contexts/GoogleMapsContext';
import { X, MapPin, Save } from 'lucide-react';

// Haversine formula to calculate distance between two points
const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371; // Radius of the Earth in kilometers
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = 
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
    Math.sin(dLon/2) * Math.sin(dLon/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  const distance = R * c; // Distance in kilometers
  return distance;
};

const FuelStationModal = ({ 
  isOpen, 
  onClose, 
  serviceLocation, 
  onFuelStationSelected,
  isSaving = false 
}) => {
  const mapContainerRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);
  const pickupMarkerRef = useRef(null);
  const currentInfoWindowRef = useRef(null);
  const { isLoaded: googleMapsLoaded, loading: googleMapsLoading, error: googleMapsError } = useGoogleMaps();

  const [fuelStations, setFuelStations] = useState([]);
  const [searchingFuelStations, setSearchingFuelStations] = useState(false);
  const [selectedFuelStation, setSelectedFuelStation] = useState(null);
  const [searchError, setSearchError] = useState(null);

  // Search for nearby fuel stations
  const searchNearbyFuelStations = useCallback(async () => {
    console.log('=== FUEL STATION SEARCH STARTED ===');
    
    if (!serviceLocation?.latitude || !serviceLocation?.longitude) {
      console.log('❌ No service location coordinates');
      return;
    }
    
    // Debug: Log the service location coordinates
    console.log('📍 Service location for fuel station search:', {
      latitude: serviceLocation.latitude,
      longitude: serviceLocation.longitude,
      address: serviceLocation.address
    });
    
    if (!googleMapsLoaded) {
      console.log('❌ Google Maps not loaded yet');
      setSearchError('Google Maps is still loading. Please wait a moment and try again.');
      return;
    }
    
    console.log('✅ Google Maps loaded');
    
    if (!window.google) {
      console.log('❌ window.google not available');
      setSearchError('Google Maps not available');
      return;
    }
    
    if (!window.google.maps) {
      console.log('❌ window.google.maps not available');
      setSearchError('Google Maps API not loaded');
      return;
    }
    
    if (!window.google.maps.places) {
      console.log('❌ window.google.maps.places not available');
      setSearchError('Google Maps Places API not loaded');
      return;
    }
    
    console.log('✅ All Google Maps APIs available');
    
    setSearchingFuelStations(true);
    setSearchError(null);
    
    try {
      console.log('🗺️ Creating temporary map...');
      // Create a temporary map for the PlacesService
      const tempDiv = document.createElement('div');
      const tempMap = new window.google.maps.Map(tempDiv, {
        center: { lat: serviceLocation.latitude, lng: serviceLocation.longitude },
        zoom: 12
      });
      
      console.log('🔍 Creating Places service...');
      const service = new window.google.maps.places.PlacesService(tempMap);
      
      console.log('⏰ Setting up timeout...');
      // Add a timeout to prevent hanging
      const searchTimeout = setTimeout(() => {
        console.log('⏰ Search timeout reached');
        setSearchingFuelStations(false);
        setFuelStations([]);
        setSearchError('Search timed out. Please try again.');
      }, 10000); // 10 second timeout

      // Use nearby search to get the closest fuel stations
      const nearbySearchRequest = {
        location: { lat: serviceLocation.latitude, lng: serviceLocation.longitude },
        type: 'gas_station',
        rankBy: window.google.maps.places.RankBy.DISTANCE // Rank by distance (removes radius requirement)
      };
      
      console.log('🚀 Making nearby search request:', nearbySearchRequest);
      
      service.nearbySearch(nearbySearchRequest, (results, status) => {
        console.log('📡 Nearby search callback received:', { status, resultsCount: results?.length });
        clearTimeout(searchTimeout);
        
        if (status === window.google.maps.places.PlacesServiceStatus.OK) {
          console.log('✅ Google Places API returned', results.length, 'results');
          
          // Debug: Check what types of places we're getting
          console.log('🔍 First 3 results types:', results.slice(0, 3).map(place => ({
            name: place.name,
            types: place.types,
            business_status: place.business_status
          })));
          
          // Filter to ensure we only get gas stations
          const gasStations = results.filter(place => 
            place.types && place.types.includes('gas_station')
          );
          
          console.log('⛽ Filtered to', gasStations.length, 'actual gas stations');
          
          // Take the closest 10 gas stations
          const closestStations = gasStations.slice(0, 10);
          console.log('🎯 Showing closest', closestStations.length, 'gas stations');
          
          setFuelStations(closestStations);
        } else if (status === window.google.maps.places.PlacesServiceStatus.ZERO_RESULTS) {
          console.log('❌ No fuel stations found in the area');
          setFuelStations([]);
          setSearchError('No fuel stations found in the area.');
        } else {
          console.log('❌ Nearby search failed with status:', status);
          setFuelStations([]);
          setSearchError('Failed to search for fuel stations. Please try again.');
        }
        setSearchingFuelStations(false);
      });
    } catch (error) {
      console.log('💥 Error in search:', error);
      setSearchingFuelStations(false);
      setSearchError('Error searching for fuel stations. Please try again.');
    }
  }, [serviceLocation, googleMapsLoaded]);

  // Initialize map when modal opens
  useEffect(() => {
    console.log('🗺️ Modal useEffect triggered:', { 
      isOpen, 
      googleMapsLoaded, 
      hasServiceLocation: !!(serviceLocation?.latitude && serviceLocation?.longitude),
      serviceLocation 
    });
    
    if (!isOpen || !googleMapsLoaded || !serviceLocation?.latitude || !serviceLocation?.longitude) {
      console.log('❌ Modal useEffect early return:', { 
        isOpen, 
        googleMapsLoaded, 
        hasServiceLocation: !!(serviceLocation?.latitude && serviceLocation?.longitude)
      });
      return;
    }

    const { latitude: lat, longitude: lng } = serviceLocation;
    
    // Create the map instance
    const mapOptions = {
      center: { lat, lng },
      zoom: 13,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: true,
    };
    
    const mapInstance = new window.google.maps.Map(mapContainerRef.current, mapOptions);
    mapInstanceRef.current = mapInstance;

    // Add pickup location marker (red dot)
    // TODO: Migrate to google.maps.marker.AdvancedMarkerElement when ready
    // Current google.maps.Marker is deprecated but still functional
    const pickupMarker = new window.google.maps.Marker({
      position: { lat, lng },
      map: mapInstance,
      icon: {
        url: 'http://maps.google.com/mapfiles/ms/icons/red-dot.png', 
        scaledSize: new window.google.maps.Size(40, 40)
      },
      title: 'Pickup Location',
      animation: window.google.maps.Animation.DROP,
    });

    // Add info window for pickup location
    const pickupInfoWindow = new window.google.maps.InfoWindow({
      content: `
        <div>
          <strong>Pickup Location</strong><br>
          ${serviceLocation.address || ''}<br>
          ${serviceLocation.city || ''}, ${serviceLocation.state || ''}
        </div>
      `
    });

    pickupMarker.addListener('click', () => {
      // Close any currently open info window
      if (currentInfoWindowRef.current) {
        currentInfoWindowRef.current.close();
      }
      
      // Open the pickup info window
      pickupInfoWindow.open(mapInstance, pickupMarker);
      currentInfoWindowRef.current = pickupInfoWindow;
    });

    pickupMarkerRef.current = pickupMarker;

    // Search for fuel stations automatically
    searchNearbyFuelStations();

    // Set initial zoom and center
    mapInstance.setZoom(13);
  }, [isOpen, googleMapsLoaded, serviceLocation, searchNearbyFuelStations]);

  // Create fuel station markers when fuel stations change
  useEffect(() => {
    if (!googleMapsLoaded || !mapInstanceRef.current || !fuelStations || fuelStations.length === 0) return;

    // Clear existing fuel station markers
    markersRef.current.forEach(marker => marker.setMap(null));
    markersRef.current = [];

    // Add fuel station markers
    fuelStations.forEach((station, index) => {
      const fuelMarker = new window.google.maps.Marker({
        position: { 
          lat: station.geometry.location.lat(), 
          lng: station.geometry.location.lng() 
        },
        map: mapInstanceRef.current,
        icon: {
          url: 'http://maps.google.com/mapfiles/ms/icons/yellow-dot.png', // Default yellow icon
          scaledSize: new window.google.maps.Size(35, 35)
        },
        title: station.name,
        animation: window.google.maps.Animation.DROP,
      });

      // Calculate distance for this station
      const distance = calculateDistance(
        serviceLocation.latitude,
        serviceLocation.longitude,
        station.geometry.location.lat(),
        station.geometry.location.lng()
      );

      // Debug: Log station data to see available fields
      console.log('Station data:', {
        name: station.name,
        formatted_address: station.formatted_address,
        vicinity: station.vicinity,
        place_id: station.place_id
      });

      // Add info window for fuel station
      const infoWindow = new window.google.maps.InfoWindow({
        content: `
          <div>
            <strong>${station.name}</strong><br>
            <strong>Address:</strong> ${station.formatted_address || station.vicinity || 'Address not available'}<br>
            <strong>Distance:</strong> ${distance.toFixed(1)} km<br>
            <strong>Rating:</strong> ${station.rating ? `${station.rating}/5` : 'N/A'}<br>
            <strong>Status:</strong> ${station.business_status === 'OPERATIONAL' ? 'Open' : 'Closed'}
          </div>
        `
      });

      fuelMarker.addListener('click', () => {
        // Close any currently open info window
        if (currentInfoWindowRef.current) {
          currentInfoWindowRef.current.close();
        }
        
        // Open the new info window
        infoWindow.open(mapInstanceRef.current, fuelMarker);
        currentInfoWindowRef.current = infoWindow;
        
        setSelectedFuelStation(station);
      });

      // Store marker with station data for easy reference
      markersRef.current.push({
        marker: fuelMarker,
        station: station,
        placeId: station.place_id
      });
    });

    // Update bounds to include all fuel stations
    if (fuelStations.length > 0) {
      const bounds = new window.google.maps.LatLngBounds();
      
      // Add pickup location
      bounds.extend({ lat: serviceLocation.latitude, lng: serviceLocation.longitude });
      
      // Add all fuel stations
      fuelStations.forEach(station => {
        bounds.extend({ 
          lat: station.geometry.location.lat(), 
          lng: station.geometry.location.lng() 
        });
      });

      // Fit bounds with padding
      if (!bounds.isEmpty()) {
        mapInstanceRef.current.fitBounds(bounds, 50);
        
        // Set a minimum zoom level
        setTimeout(() => {
          if (mapInstanceRef.current.getZoom() > 14) {
            mapInstanceRef.current.setZoom(14);
          }
        }, 100);
      }
    }
  }, [googleMapsLoaded, fuelStations, serviceLocation]);

  // Update marker icons when selection changes (without recreating markers)
  useEffect(() => {
    if (!markersRef.current || markersRef.current.length === 0) return;

    markersRef.current.forEach(markerData => {
      const { marker, placeId } = markerData;
      const isSelected = selectedFuelStation?.place_id === placeId;
      
      // Update icon without recreating the marker
      marker.setIcon({
        url: isSelected 
          ? 'http://maps.google.com/mapfiles/ms/icons/orange-dot.png'
          : 'http://maps.google.com/mapfiles/ms/icons/yellow-dot.png',
        scaledSize: new window.google.maps.Size(35, 35)
      });
    });
  }, [selectedFuelStation]);

  // Handle save button click
  const handleSave = () => {
    if (selectedFuelStation) {
      // Use formatted_address as primary, fallback to vicinity
      const address = selectedFuelStation.formatted_address || selectedFuelStation.vicinity || '';
      
      // Extract city and state from address - handle cases where address might be undefined
      let city = '';
      let state = '';
      let pincode = null;
      
      if (address) {
        const addressParts = address.split(',').map(part => part.trim());
        
        // Extract postcode (NZ 4-digit, or legacy 6-digit)
        const pincodeMatch = address.match(/\b\d{4}\b/) || address.match(/\b\d{6}\b/);
        if (pincodeMatch) {
          pincode = pincodeMatch[0];
        }
        
        // Extract region (NZ regions / cities commonly used as region)
        const stateNames = [
          'Auckland', 'Wellington', 'Canterbury', 'Waikato', 'Bay of Plenty',
          'Otago', 'Manawatū-Whanganui', 'Manawatu-Whanganui', 'Hawke\'s Bay',
          'Taranaki', 'Northland', 'Southland', 'Nelson', 'Marlborough',
          'Tasman', 'Gisborne', 'West Coast',
        ];
        for (const part of addressParts) {
          if (stateNames.includes(part)) {
            state = part;
            break;
          }
        }
        
        // Extract city (usually second to last part, before region/postcode)
        if (addressParts.length >= 2) {
          // Look for parts that might be city names (not region, postcode, or country)
          for (let i = addressParts.length - 3; i >= 0; i--) {
            const part = addressParts[i];
            if (part && part.length > 0 && 
                !part.match(/^\d{4,6}$/) && 
                !stateNames.includes(part) && 
                !part.match(/^(New Zealand|NZ|Aotearoa|India|IN)$/i)) {
              city = part;
              break;
            }
          }
          // If no city found, use second to last part
          if (!city && addressParts.length >= 2) {
            city = addressParts[addressParts.length - 2] || '';
          }
        }
      }
      
      // Format fuel location data according to LocationInfo model
      const fuelLocationData = {
        latitude: parseFloat(selectedFuelStation.geometry.location.lat()),
        longitude: parseFloat(selectedFuelStation.geometry.location.lng()),
        address: address,
        landmark: selectedFuelStation.name,
        city: city,
        state: state,
        pincode: pincode
      };
      
      // Validate required fields
      if (!fuelLocationData.address || fuelLocationData.address.trim() === '') {
        setSearchError('Fuel station address is required');
        return;
      }
      
      if (isNaN(fuelLocationData.latitude) || isNaN(fuelLocationData.longitude)) {
        setSearchError('Invalid coordinates for fuel station');
        return;
      }
      
      onFuelStationSelected(fuelLocationData);
    }
  };

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setFuelStations([]);
      setSelectedFuelStation(null);
      setSearchingFuelStations(false);
      setSearchError(null);
      
      // Close any open info window
      if (currentInfoWindowRef.current) {
        currentInfoWindowRef.current.close();
        currentInfoWindowRef.current = null;
      }
      
      // Clear markers
      markersRef.current.forEach(markerData => markerData.marker.setMap(null));
      markersRef.current = [];
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center space-x-3">
            <MapPin className="w-6 h-6 text-orange-600" />
            <h2 className="text-xl font-bold">Select Fuel Station</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 flex flex-col">
          {/* Map Container */}
          <div className="flex-1 relative">
            {googleMapsLoading ? (
              <div className="flex items-center justify-center h-full bg-gray-100">
                <div className="text-gray-600">Loading map...</div>
              </div>
            ) : googleMapsError ? (
              <div className="flex items-center justify-center h-full bg-gray-100">
                <div className="text-red-600">Error loading map: {googleMapsError}</div>
              </div>
            ) : (
              <div className="relative h-full">
                {/* Map Legend */}
                <div className="absolute top-4 left-4 bg-white p-3 rounded-lg shadow-md text-xs z-10">
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center">
                      <div className="w-3 h-3 bg-red-500 rounded-full mr-1"></div>
                      <span>Pickup Location</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-3 h-3 bg-yellow-500 rounded-full mr-1"></div>
                      <span>Fuel Stations</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-3 h-3 bg-orange-500 rounded-full mr-1"></div>
                      <span>Selected</span>
                    </div>
                  </div>
                </div>

                {/* Search Status */}
                {searchingFuelStations && (
                  <div className="absolute top-4 right-4 bg-blue-500 text-white px-4 py-2 rounded-lg shadow-md z-10">
                    Searching for fuel stations...
                  </div>
                )}

                {/* Error Message */}
                {searchError && (
                  <div className="absolute top-16 right-4 bg-red-500 text-white px-4 py-2 rounded-lg shadow-md z-10 max-w-xs">
                    <div className="text-sm">{searchError}</div>
                    <button 
                      onClick={searchNearbyFuelStations}
                      className="mt-2 text-xs bg-white text-red-500 px-2 py-1 rounded hover:bg-gray-100"
                    >
                      Try Again
                    </button>
                  </div>
                )}

                {/* Map */}
                <div 
                  ref={mapContainerRef} 
                  className="w-full h-full rounded-b-lg"
                />
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="p-6 border-t bg-gray-50">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                {selectedFuelStation ? (
                  <div className="text-sm">
                    <span className="font-medium text-green-600">✓ Selected:</span>
                    <span className="ml-2 font-medium">{selectedFuelStation.name}</span>
                    <span className="ml-2 text-gray-600">({selectedFuelStation.vicinity})</span>
                    <span className="ml-2 text-blue-600">
                      {calculateDistance(
                        serviceLocation.latitude,
                        serviceLocation.longitude,
                        selectedFuelStation.geometry.location.lat(),
                        selectedFuelStation.geometry.location.lng()
                      ).toFixed(1)} km away
                    </span>
                  </div>
                ) : (
                  <div className="text-sm text-gray-600">
                    Click on a fuel station marker to select it
                  </div>
                )}
              </div>
              
              <div className="flex items-center space-x-3">
                <button
                  onClick={onClose}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={!selectedFuelStation || isSaving}
                  className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
                >
                  <Save className="w-4 h-4" />
                  <span>{isSaving ? 'Saving...' : 'Save Fuel Station'}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FuelStationModal; 